import * as Sentry from "@sentry/react";
import { QueryClient,QueryClientProvider,useQuery,useQueryClient } from "@tanstack/react-query";
import { useEffect,useState } from "react";
import "./App.scss";
import { ChildActivityHeader,useChildPersona } from "./components/activity-header/ChildActivityHeader";
import { ActivityNotice } from "./components/activity-notice/ActivityNotice";
import { useChildAppearance, updateChildCaches } from "./hooks/use-child-appearance";
import ChildStats from "./components/child-stats/ChildStats";
import { childThemeStyle } from "./components/child-theme/ChildTheme";
import FamilyGate from "./components/family-gate/FamilyGate";
import KidsHome from "./components/kids-home/KidsHome";
import LoginForm from "./components/login/LoginForm";
import ParentDashboard from "./components/parent-dashboard/ParentDashboard";
import AccessibilityStatement from "./components/privacy/AccessibilityStatement";
import PrivacyPolicy from "./components/privacy/PrivacyPolicy";
import TermsOfService from "./components/privacy/TermsOfService";
import "./components/privacy/privacy.scss";
import QuizWidget from "./components/quiz/QuizWidget";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import { INITIAL_SESSION,QUIZ_SESSION_KEY } from "./hooks/use-quiz-session";
import type { ChildProfile } from "./types";

import { ReadingActivity } from "./features/reading/ReadingActivity";
import "./styles/brand.scss";
import type { ReadingActor } from "./features/reading/api";
import { navigate,readingReturnPath,rememberReadingReturn,useLocation } from "./lib/navigation";
import { syncReadingReplay } from "./sentry";

type AuthUser = { id: string; name: string; email: string | null; familyId: string | null };

// ── Shared QueryClient ────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 60_000,
    },
  },
});

function usePageTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} | topiqz` : "topiqz";
  }, [title]);
}

function CrashFallback() {
  return <ActivityNotice error onRetry={() => window.location.reload()}>משהו השתבש. אפשר לנסות שוב.</ActivityNotice>;
}

class ChildLinkError extends Error {
  constructor(public status: number) { super("child_link_failed"); }
}

export type ChildGender = "boy" | "girl" | "";

// ── Parent dashboard (after Google login) ──

function ParentApp() {
  usePageTitle("ניהול ילדים");
  const queryClient = useQueryClient();
  const [statsChild, setStatsChild] = useState<ChildProfile | null>(null);

  const { data: children = [], isPending, error: childrenError, refetch: retryChildren } = useQuery<ChildProfile[]>({
    queryKey: ["children"],
    queryFn: async () => {
      const res = await fetch("/api/children");
      if (!res.ok) throw new Error(`Failed to load children: ${res.status}`);
      return res.json();
    },
  });

  useEffect(() => {
    if (children.length > 0) {
      Sentry.addBreadcrumb({ category: "parent.children-load", message: `Loaded ${children.length} children`, level: "info" });
    }
  }, [children]);

  useEffect(() => {
    if (childrenError) Sentry.captureException(childrenError, { tags: { flow: "parent" } });
  }, [childrenError]);

  if (isPending) return <ActivityNotice loading>טוענים…</ActivityNotice>;

  if (childrenError) return <ActivityNotice error onRetry={() => void retryChildren()}>לא הצלחנו לטעון את הילדים.</ActivityNotice>;

  if (statsChild) {
    return <ChildStats child={statsChild} onBack={() => setStatsChild(null)} />;
  }

  return (
    <ParentDashboard
      children={children}
      onChildCreated={(child) =>
        queryClient.setQueryData<ChildProfile[]>(["children"], (prev = []) => [...prev, child])
      }
      onChildUpdated={(child) =>
        queryClient.setQueryData<ChildProfile[]>(["children"], (prev = []) =>
          prev.map((c) => (c.id === child.id ? child : c))
        )
      }
      onChildDeleted={(id) =>
        queryClient.setQueryData<ChildProfile[]>(["children"], (prev = []) =>
          prev.filter((c) => c.id !== id)
        )
      }
      onViewStats={setStatsChild}
    />
  );
}

// ── Child quiz (magic link entry) ──

function PlayApp({ token, reading, enabled }: { token: string; reading: boolean; enabled: boolean }) {
  usePageTitle(reading ? "קריאה" : "אוצר מילים");
  const queryClient = useQueryClient();

  // Load child profile via magic link
  const { data: child, isPending, isError, isFetching: authenticating, error: playError, refetch: retryPlay } = useQuery<ChildProfile>({
    queryKey: ["play", token],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/play/${token}`, { method: "POST", signal });
      if (!res.ok) throw new ChildLinkError(res.status);
      const profile: ChildProfile = { ...await res.json(), magicToken: token };
      const previous = queryClient.getQueryData<string>(["active-child"]);
      if (previous !== profile.id) queryClient.setQueryData(QUIZ_SESSION_KEY, INITIAL_SESSION);
      queryClient.setQueryData(["active-child"], profile.id);
      return profile;
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (child) {
      Sentry.addBreadcrumb({ category: "play.magic-link", message: `Child authenticated: ${child.name}`, level: "info" });
      Sentry.setUser({ id: child.id, username: child.name });
    }
  }, [child]);

  useEffect(() => {
    if (playError) Sentry.captureException(playError, { tags: { flow: "play" } });
  }, [playError]);

  if (isPending) return <ActivityNotice loading>טוענים…</ActivityNotice>;

  if (authenticating) return <ActivityNotice loading>טוענים את הפרופיל…</ActivityNotice>;

  if (isError || !child) {
    if (playError instanceof ChildLinkError && playError.status === 404) {
      return <ActivityNotice error>הקישור לא תקין. בקשו מההורים קישור חדש.</ActivityNotice>;
    }
    return <ActivityNotice error onRetry={() => void retryPlay()}>לא הצלחנו לטעון את הפרופיל.</ActivityNotice>;
  }

  return <ChildActivity key={child.id} child={child} reading={reading} enabled={enabled} />;
}

/** One child shell and profile save path, regardless of how the child entered. */
function ChildActivity({ child, reading, enabled = true }: {
  child: ChildProfile; reading: boolean; enabled?: boolean;
}) {
  const client = useQueryClient();
  const [personaProfileId] = useChildPersona(child.id);
  const appearance = useChildAppearance(child);
  const vocabularyUrl = child.magicToken ? `/play/${child.magicToken}` : "/kids";
  useEffect(() => {
    if (client.getQueryData(["active-child"]) !== child.id) {
      client.setQueryData(QUIZ_SESSION_KEY, INITIAL_SESSION);
      client.setQueryData(["active-child"], child.id);
    }
  }, [child.id, client]);
  useEffect(() => {
    const title = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (title) title.content = child.name;
    if (manifest) manifest.href = child.magicToken ? `/api/play/${child.magicToken}/manifest.json` : "/api/manifest.json";
    return () => {
      if (title) title.content = "topiqz";
      if (manifest) manifest.href = "/api/manifest.json";
    };
  }, [child.name, child.magicToken]);
  const header = <ChildActivityHeader child={child} activity={reading ? "reading" : "vocabulary"}
    otherUrl={reading ? vocabularyUrl : enabled ? `${vocabularyUrl}?activity=reading` : undefined}
    onProfileChange={appearance.save} saving={appearance.saving} saveError={appearance.error} onRetrySave={appearance.retry} />;
  if (reading) {
    if (!enabled) return <ReadingUnavailable vocabularyUrl={vocabularyUrl} />;
    if (!child.familyId) return <ActivityNotice error>יש להשלים את הכניסה המשפחתית. <a href="/kids">בחירת ילד</a></ActivityNotice>;
    return <Sentry.ErrorBoundary fallback={<CrashFallback />}><ReadingActivity
      actor={{ actor: "child", familyId: child.familyId, child }} vocabularyUrl={vocabularyUrl} header={header} /></Sentry.ErrorBoundary>;
  }
  return <div className="App child-theme" style={childThemeStyle(child)}>
    <Sentry.ErrorBoundary fallback={<CrashFallback />}><LiveAPIProvider>
      <div className="app-shell" dir="rtl">{header}
        <main id="main-content" className="content-area"><QuizWidget personaProfileId={personaProfileId}
          onAdvanced={currentStep => updateChildCaches(client, { ...child, currentStep })} /></main>
      </div>
    </LiveAPIProvider></Sentry.ErrorBoundary>
  </div>;
}

// ── Parent flow with auth gate ──

function ParentFlow({ readingStory = false }: { readingStory?: boolean }) {
  usePageTitle(readingStory ? "קריאה" : "הורים");
  const queryClient = useQueryClient();

  const { data: authData, isPending, isError: authError, refetch: retryAuth } = useQuery<{ ok: boolean; user?: AuthUser }>({
    queryKey: ["auth"],
    queryFn: async () => {
      const res = await fetch("/api/auth/check", { method: "POST" });
      if (res.status === 401) return { ok: false };
      if (!res.ok) throw new Error("auth_check_failed");
      const data = await res.json();
      Sentry.addBreadcrumb({ category: "parent.auth-check", message: "Parent authenticated", level: "info" });
      Sentry.setUser({ id: data.user?.id ?? "parent" });
      return data;
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (isPending) return <ActivityNotice loading>טוענים…</ActivityNotice>;

  if (authError) return <ActivityNotice error onRetry={() => void retryAuth()}>לא הצלחנו לבדוק את הכניסה.</ActivityNotice>;

  if (!authData?.ok || !authData.user) {
    return (
      <div className="App">
        <ParentLogin />
      </div>
    );
  }

  if (!authData.user.familyId) {
    return (
      <div className="App">
        <FamilyGate
          userName={authData.user.name}
          onComplete={() => queryClient.invalidateQueries({ queryKey: ["auth"] })}
        />
      </div>
    );
  }

  if (readingStory) return <ReadingActivity actor={{ actor: "parent", familyId: authData.user.familyId }} vocabularyUrl="/kids" />;
  return <ParentApp />;
}

// ── Auth-gated /kids route ──

function KidsFlow() {
  usePageTitle("בחירת ילד");
  const queryClient = useQueryClient();

  const { data: authData, isPending: authPending, isError: authError, refetch: retryAuth } = useQuery<{ ok: boolean; user?: AuthUser }>({
    queryKey: ["auth"],
    queryFn: async () => {
      const res = await fetch("/api/auth/check", { method: "POST" });
      if (res.status === 401) return { ok: false };
      if (!res.ok) throw new Error("auth_check_failed");
      return res.json();
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  const hasFamily = authData?.ok && authData.user?.familyId;

  const { data: children = [], isPending: childrenPending, isError: childrenError, refetch: retryChildren } = useQuery<ChildProfile[]>({
    queryKey: ["children"],
    queryFn: async () => {
      const res = await fetch("/api/children");
      if (!res.ok) throw new Error("children_load_failed");
      return res.json();
    },
    enabled: !!hasFamily,
  });

  if (authPending) return <ActivityNotice loading>טוענים…</ActivityNotice>;

  if (authError) return <ActivityNotice error onRetry={() => void retryAuth()}>לא הצלחנו לבדוק את הכניסה.</ActivityNotice>;

  if (!authData?.ok) {
    return (
      <div className="App">
        <ParentLogin />
      </div>
    );
  }

  if (!authData.user?.familyId) {
    return (
      <div className="App">
        <FamilyGate
          userName={authData.user?.name ?? ""}
          onComplete={() => queryClient.invalidateQueries({ queryKey: ["auth"] })}
        />
      </div>
    );
  }

  if (childrenPending) return <ActivityNotice loading>טוענים את הילדים…</ActivityNotice>;
  if (childrenError) return <ActivityNotice error onRetry={() => void retryChildren()}>לא הצלחנו לטעון את הילדים.</ActivityNotice>;
  return <KidsHome children={children} onBack={() => navigate("/")} />;
}

// ── Root: route between parent and child ──

export function AppRoutes() {
  const location = useLocation();
  useEffect(syncReadingReplay, [location]);
  const path = location.split("?")[0];
  const { data: features, isPending: featuresPending, isError: featuresError, refetch: retryFeatures } = useQuery<{ reading: boolean }>({ queryKey: ["features"], queryFn: async () => { const res = await fetch("/api/features"); if (!res.ok) throw new Error("features_load_failed"); return res.json(); } });
  const returnTarget = path === "/" ? readingReturnPath(sessionStorage.getItem("reading-return")) : null;
  useEffect(() => {
    if (path === "/") {
      const target = readingReturnPath(sessionStorage.getItem("reading-return"));
      sessionStorage.removeItem("reading-return");
      if (target) navigate(target, true);
    }
  }, [path]);
  if (path === "/privacy") {
    return <PrivacyPolicy />;
  }

  if (path === "/terms") {
    return <TermsOfService />;
  }

  if (path === "/accessibility") {
    return <AccessibilityStatement />;
  }

  if (returnTarget) return <ActivityNotice loading>חוזרים לפעילות…</ActivityNotice>;
  if (featuresPending) return <ActivityNotice loading>טוענים את topiqz…</ActivityNotice>;
  if (featuresError) return <ActivityNotice error onRetry={() => void retryFeatures()}>לא הצלחנו לטעון את הפעילויות.</ActivityNotice>;
  const enabled = features?.reading === true;
  if (path === "/reading") {
    if (!enabled) return <ReadingUnavailable />;
    return <SharedReading />;
  }
  const playMatch = path.match(/^\/play\/([a-f0-9-]+)$/i);

  if (playMatch) {
    return (
      <Sentry.ErrorBoundary
        fallback={<CrashFallback />}
        beforeCapture={(scope) => scope.setTag("flow", "play")}
      >
        <PlayApp key={playMatch[1]} token={playMatch[1]} reading={new URLSearchParams(window.location.search).get("activity") === "reading"} enabled={enabled} />
      </Sentry.ErrorBoundary>
    );
  }

  if (path === "/kids" && new URLSearchParams(window.location.search).get("activity") === "reading" && !enabled) return <ReadingUnavailable />;

  if (path === "/kids") {
    return (
      <Sentry.ErrorBoundary
        fallback={<CrashFallback />}
        beforeCapture={(scope) => scope.setTag("flow", "kids")}
      >
        <KidsFlow />
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <Sentry.ErrorBoundary
      fallback={<CrashFallback />}
      beforeCapture={(scope) => scope.setTag("flow", "parent")}
    >
      <ParentFlow />
    </Sentry.ErrorBoundary>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppRoutes />
    </QueryClientProvider>
  );
}

function ParentLogin() {
  useEffect(() => { rememberReadingReturn(); }, []);
  return <LoginForm />;
}
function ReadingUnavailable({ vocabularyUrl = "/kids" }: { vocabularyUrl?: string }) {
  return <ActivityNotice>תרגול הקריאה אינו זמין כרגע. <a href={vocabularyUrl}>מעבר לאוצר מילים</a></ActivityNotice>;
}
function SharedReading() {
  usePageTitle("קריאה");
  const { data, isPending, isError, refetch } = useQuery<{ context?: ReadingActor; status: number }>({
    queryKey: ["reading-context"], queryFn: async () => {
      const res = await fetch("/api/reading/context");
      return { status: res.status, context: res.ok ? await res.json() : undefined };
    }, retry: false, staleTime: 0,
  });
  if (isPending) return <ActivityNotice loading>טוענים את הקריאה…</ActivityNotice>;
  if (isError || data && ![200, 401, 403].includes(data.status)) return <ActivityNotice error onRetry={() => void refetch()}>לא הצלחנו לפתוח את הקריאה.</ActivityNotice>;
  if (!data?.context) return <ParentFlow readingStory />;
  if (data.context.actor === "child" && data.context.child) return <SharedChildReading actor={data.context} />;
  return <ReadingActivity key={`${data.context.actor}:${data.context.child?.id ?? data.context.familyId}`} actor={data.context} vocabularyUrl="/kids" />;
}

function SharedChildReading({ actor }: { actor: ReadingActor }) {
  const child: ChildProfile = { ...actor.child!, familyId: actor.familyId, magicToken: actor.child!.magicToken ?? "" };
  return <ChildActivity key={child.id} child={child} reading />;
}
